import { createSlice, createSelector } from '@reduxjs/toolkit'
import { successSession, successRegionId } from './sessionSlice'
import { successOrderCreate } from './checkoutSlice'

const public_key = 'qlwkxuquiw5xdniuwhx3xx3yygsdT57tyg'

const sha256 = async (text, { toSHA256 } = {}) => {
  const message = public_key + text
  if (toSHA256) {
    return toSHA256(message)
  }
  const msgBuffer = new TextEncoder('utf-8').encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => ('00' + b.toString(16)).slice(-2)).join('');

  return hashHex;
}

export const PARAMS = {
  FIRST_NAME: 'first_name',
  LAST_NAME: 'last_name',
  PHONE: 'phone',
  EMAIL: 'email',
  SEX: 'sex',
  SEX_WOMEN: 'women',
  SEX_MEN: 'men',
  BIRTHDAY: 'birthday',
  DISCOUNT_CARD: 'discount_card',
  CLOTHING_SIZE: 'clothing_size',
  SHOE_SIZE: 'shoe_size',
  HEIGHT: 'height',
  WEIGHT: 'weight',
  BRAND_IDS: 'brand_ids',
  CURRENCY: 'currency',
}

const initialState = {
  user: null,
  loggedIn: false,
  loggingIn: false,
  loggingOut: false,
  removingProfile: false,
  codeSending: false,
  codeSent: false,
  error: null,
  removingProfileError: null,
  hasBanner: false,
  redirectUrl: { href: '/', asPath: '/', query: {} },
  hasNewProducts: false,
  isNewUser: false,
  subscribeError: null,
  emailValidateError: null,
  phoneValidateError: null,
  returnableItem: null,
  hasGotIP: false,
  favoriteBrands: {
    data: [],
    loading: false,
    error: null,
  },
  profile: {
    loading: false,
    error: null,
  },
  refund: {
    loading: false,
    error: null,
  },
  refundInfo: {
    data: null,
    loading: false,
    error: null,
  },
  countriesOfDelivery: {
    data: [],
    loading: false,
    error: null,
  },
  ordersHistory: {
    data: null,
    loading: false,
    error: null,
  },
  refundsHistory: {
    data: null,
    loading: false,
    error: null,
  },
  returnableProducts: {
    data: null,
    loading: false,
    error: null,
  },
  order: {
    data: null,
    loading: false,
    error: null,
  },
  discount: {
    data: null,
    loading: false,
    error: null,
  },
}

const user = createSlice({
  name: 'user',
  initialState: initialState,
  reducers: {
    requestChangeFavoriteBrands(state) {
      state.favoriteBrands.loading = true
      state.error = null
    },
    successChangeFavoriteBrands(state, { payload }) {
      state.favoriteBrands.data = payload
      state.favoriteBrands.loading = false
      state.error = null
    },
    failureChangeFavoriteBrands(state, { payload }) {
      state.favoriteBrands.loading = false
      state.error = payload
    },
    requestCode(state) {
      state.codeSending = true
      state.error = null
    },
    successCode(state) {
      state.codeSending = false
      state.codeSent = true
      state.error = null
    },
    failureCode(state, { payload }) {
      state.codeSending = false
      state.codeSent = false
      state.error = payload
    },
    resetSendCodeState(state) {
      state.codeSent = false
    },
    requestLogin(state) {
      state.loggingIn = true
    },
    successLogin(state, { payload }) {
      const user = payload?.client
      state.user = user
      state.favoriteBrands.data = payload.favorite_brands

      state.loggingIn = false
      state.loggedIn = true
      state.error = null
    },
    failureLogin(state, { payload }) {
      state.loggingIn = false
      state.error = payload
    },
    requestLogout(state) {
      state.loggingOut = true
    },
    successLogout(state) {
      state.user = null
      state.loggedIn = false
      state.loggingOut = false
    },
    failureLogout(state, { payload }) {
      state.loggingOut = false
      state.error = payload
    },
    requestRemoveProfile(state) {
      state.removingProfile = true
    },
    successRemoveProfile(state) {
      state.removingProfile = false
    },
    failureRemoveProfile(state, { payload }) {
      state.removingProfile = false
      state.removingProfileError = payload
    },
    changeRedirectUrl(state, { payload }) {
      state.redirectUrl = payload || { href: '/', asPath: '/', query: {} }
    },
    changeReturnableItem(state, { payload }) {
      state.returnableItem = payload ? payload : null
    },
    requestProfileUpdate(state) {
      state.profile.loading = true
      state.profile.error = null
    },
    successProfileUpdate(state, { payload }) {
      state.user = payload.user
      state.profile.loading = false
      state.profile.error = null
    },
    failureProfileUpdate(state, { payload }) {
      state.profile.loading = false
      state.profile.error = payload
    },
    changeNewProducts(state, { payload }) {
      state.hasNewProducts = payload
    },
    changeNewUserStatus(state, { payload }) {
      state.isNewUser = payload
    },
    requestUserOrders(state) {
      state.ordersHistory.loading = true
      state.ordersHistory.error = null
    },
    successUserOrders(state, { payload }) {
      if (payload) {
        const { mergeResult = false, ...data } = payload
        if (mergeResult) {
          const mergedItems = [
            ...(state.ordersHistory?.data?.data || []),
            ...(data?.data || [])
          ]
          state.ordersHistory.data = {
            ...data,
            data: mergedItems
          }
        } else {
          state.ordersHistory.data = data
        }
      }
      state.ordersHistory.loading = false
      state.ordersHistory.error = null
    },
    failureUserOrders(state, { payload }) {
      state.ordersHistory.loading = false
      state.ordersHistory.error = payload
    },
    requestUserRefunds(state) {
      state.refundsHistory.loading = true
      state.refundsHistory.error = null
    },
    successUserRefunds(state, { payload }) {
      if (payload) {
        const { mergeResult = false, ...data } = payload

        if (mergeResult) {
          const mergedItems = [
            ...(state.refundsHistory?.data?.data || []),
            ...(data?.data || [])
          ]
          state.refundsHistory.data = {
            ...data,
            data: mergedItems
          }
        } else {
          state.refundsHistory.data = data
        }
      }
      state.refundsHistory.loading = false
      state.refundsHistory.error = null
    },
    failureUserRefunds(state, { payload }) {
      state.refundsHistory.loading = false
      state.refundsHistory.error = payload
    },
    requestReturnableProducts(state) {
      state.returnableProducts.loading = true
      state.returnableProducts.error = null
    },
    successReturnableProducts(state, { payload }) {
      if (payload) {
        const { mergeResult = false, ...data } = payload

        if (mergeResult) {
          const mergedItems = [
            ...(state.returnableProducts?.data?.data || []),
            ...(data?.data || [])
          ]
          state.returnableProducts.data = {
            ...data,
            data: mergedItems
          }
        } else {
          state.returnableProducts.data = data
        }
      }
      state.returnableProducts.loading = false
      state.returnableProducts.error = null
    },
    failureReturnableProducts(state, { payload }) {
      state.returnableProducts.loading = false
      state.returnableProducts.error = payload
    },
    requestSendRefund(state) {
      state.refund.loading = true
    },
    successSendRefund(state, { payload }) {
      state.refund.loading = false
      state.refund.error = null
    },
    failureSendRefund(state, { payload }) {
      state.refund.loading = false
      state.refund.error = payload
    },
    requestCountriesOfDelivery(state) {
      state.countriesOfDelivery.loading = true
      state.countriesOfDelivery.error = null
    },
    successCountriesOfDelivery(state, { payload }) {
      state.countriesOfDelivery.data = payload
      state.countriesOfDelivery.loading = false
      state.countriesOfDelivery.error = null
    },
    failureCountriesOfDelivery(state, { payload }) {
      state.countriesOfDelivery.loading = false
      state.countriesOfDelivery.error = payload
    },
    requestRefundInfo(state) {
      state.refundInfo.loading = true
    },
    successRefundInfo(state, { payload }) {
      if (payload) {
        state.refundInfo.data = payload
      }
      state.refundInfo.loading = false
      state.refundInfo.error = null
    },
    failureRefundInfo(state, { payload }) {
      state.refundInfo.data = null
      state.refundInfo.loading = false
      state.refundInfo.error = payload
    },
    requestOrder(state) {
      state.order.loading = true
      state.order.error = null
    },
    successOrder(state, { payload }) {
      if (payload) {
        state.order.data = payload
      }
      state.order.loading = false
      state.order.error = null
    },
    failureOrder(state, { payload }) {
      state.order.data = null
      state.order.loading = false
      state.order.error = payload
    },
    successGotIP(state) {
      state.hasGotIP = true
    },
    failureSubscribe(state, { payload }) {
      state.subscribeError = payload
    },
    failureValidateEmail(state, { payload }) {
      state.emailValidateError = payload
    },
    failureValidatePhone(state, { payload }) {
      state.phoneValidateError = payload
    },
    resetProfileError(state) {
      state.profile.error = null
    },
    requestDiscount(state) {
      state.discount.loading = true
      state.discount.error = null
    },
    successDiscount(state, { payload }) {
      if (payload) {
        state.discount.data = payload
      }
      state.discount.loading = false
      state.discount.error = null
    },
    failureDiscount(state, { payload }) {
      state.discount.data = null
      state.discount.loading = false
      state.discount.error = payload
    },
    changeBanner(state, { payload }) {
      state.hasBanner = payload
    }
  },
  extraReducers: {
    [successSession]: (state, { payload }) => {
      const client = payload?.client ?? null
      const favoriteBrands = payload?.favorite_brands ?? null

      if (client) {
        state.user = client
        state.loggedIn = true
      }

      if (favoriteBrands) {
        state.favoriteBrands.data = favoriteBrands
      }
    },
    [successOrderCreate]: (state, { payload }) => {
      const client = payload?.client ?? null
      const cleanOrders = payload?.cleanOrders ?? true

      if (client) {
        state.user = client
      }
      if (cleanOrders) {
        state.ordersHistory.data = null
      }
    },
  },
})

export const sendCode = ({ onSuccess, toSHA256, ...params }) => async (dispatch, getState, { api }) => {
  dispatch(requestCode())
  const hash = await sha256(params.phone, { toSHA256 })
  const response = await api.sendSmsCode({ ...params, hash })
  const result = response.result

  if (result === 'success') {
    dispatch(successCode())
    if (onSuccess) onSuccess()
  } else {
    dispatch(failureCode(response))
  }
}

export const login = ({ onSuccess, ...params }) => async (dispatch, getState, { api }) => {
  dispatch(requestLogin())

  const response = await api.login(params)
  const result = response.result

  if (result === 'success') {
    const hasNewProducts = response?.hasNewProducts ?? false
    const isNewUser = response?.registered ?? false
    const session = response.session
    dispatch(changeNewProducts(hasNewProducts))
    dispatch(changeNewUserStatus(isNewUser))
    dispatch(successRegionId({ regionId: session.region_id }));

    dispatch(successLogin(session))
    if (onSuccess) onSuccess({ isNewUser, hasNewProducts, user: session.client })
  } else {
    dispatch(failureLogin(response))
  }
}

export const pushToken = params => async (dispatch, getState, { api }) => {
  try {
    const response = await api.pushToken(params)
  } catch (e) { }
}

export const getIP = () => async (dispatch, getState, { api }) => {
  dispatch(successGotIP())
}

export const deletePushToken = params => async (dispatch, getState, { api }) => {
  try {
    const response = await api.deletePushToken(params)
  } catch (e) {
  }
}

export const logout = (onSuccess, onError) => async (dispatch, getState, { api }) => {
  dispatch(requestLogout())

  const response = await api.logout()
  const result = response.result

  if (result === 'success') {
    dispatch(successLogout())
    if (onSuccess) onSuccess()
  } else {
    dispatch(failureLogout(response))
    onError?.(response)
  }
}

export const removeProfile = ({ onSuccess, onError } = {}) => async (dispatch, getState, { api }) => {
  dispatch(requestRemoveProfile())
  const response = await api.removeProfile()
  const result = response.result

  if (result === 'success') {
    dispatch(successRemoveProfile())
    onSuccess?.()
  } else {
    dispatch(failureRemoveProfile(response))
    onError?.(response)
  }
}

export const updateProfile = ({ params, onSuccess, onError }) => async (dispatch, getState, { api }) => {
  try {
    dispatch(requestProfileUpdate())
    const response = await api.userProfileUpdate(params)
    const { result, ...data } = response

    if (result === 'success') {
      const user = response.client
      dispatch(successProfileUpdate({ user }))
      onSuccess && onSuccess(user)
    } else {
      dispatch(failureProfileUpdate(response))
      onError?.(response)
    }
  } catch (err) {
    dispatch(failureProfileUpdate(err.toString()))
    onError?.(err)
  }
}

export const emailVerify = ({ onSuccess = null, onError = null }) => async (dispatch, getState, { api }) => {
  try {
    const response = await api.emailVerify()
    const { result } = response;

    if (result === 'success') {
      onSuccess?.()
    } else {
      onError?.(response)
    }
  } catch (err) {
    onError?.(err)
  }
}

export const deleteAddress = params => async (dispatch, getState, { api }) => {
  try {
    const response = await api.addressDelete(params)

    const result = response.result

    if (result === 'success') {
      const user = response.client
      dispatch(successProfileUpdate({ user }))
    } else {
      dispatch(failureProfileUpdate(response))
    }
  } catch (err) {
    dispatch(failureProfileUpdate(err.toString()))
  }
}

export const subscribeByEmail = ({ email, mindboxDeviceUUID, onSuccess }) => async (dispatch, getState, { api }) => {
  try {
    const response = await api.subscribeByEmail({ email, mindboxDeviceUUID })
    const result = response?.result

    if (result === 'success') {
      if (onSuccess) onSuccess()
    } else {
      dispatch(failureSubscribe(response))
    }
  } catch (err) {
    dispatch(failureSubscribe(err.toString()))
  }
}

export const validateEmail = ({ email, onSuccess }) => async (dispatch, getState, { api }) => {
  try {
    const response = await api.emailValidate({ email })
    const result = response?.result

    if (result === 'success') {
      dispatch(failureValidateEmail(null))
      if (onSuccess) onSuccess()
    } else {
      dispatch(failureValidateEmail(response))
    }
  } catch (err) {
    dispatch(failureValidateEmail(err.toString()))
  }
}

export const sendSubscription = ({ data, onSuccess, onError }) => async (dispatch, getState, { api }) => {
  try {
    const response = await api.subscriptionsSubscribe(data)
    const result = response?.result

    if (result === 'success') {
      if (onSuccess) onSuccess()
    } else {
      if (onError) onError(response?.errors)
    }
  } catch (err) {
    onError()
  }
}

export const getSubscriptions = ({ originalError = false, onSuccess, onError }) => async (dispatch, getState, { api }) => {
  try {
    const response = await api.profileSubscriptions()
    const result = response?.result

    if (result === 'success') {
      if (onSuccess) onSuccess(response?.data)
    } else {
      if (onError) onError(originalError ? response : response?.errors)
    }
  } catch (err) {
    if (onError) onError()
  }
}

export const validatePhone = ({ phone, onSuccess }) => async (dispatch, getState, { api }) => {
  try {
    const response = await api.phoneValidate({ phone })
    const result = response?.result

    if (result === 'success') {
      dispatch(failureValidatePhone(null))
      if (onSuccess) onSuccess()
    } else {
      dispatch(failureValidatePhone(response))
    }
  } catch (err) {
    dispatch(failureValidatePhone(err.toString()))
  }
}

export const changeBannerState = (newState) => (dispatch) => {
  dispatch(changeBanner(newState));
}

export const fetchUserOrders = ({ mergeResult, ...params } = {}) => async (dispatch, getState, { api }) => {
  try {
    dispatch(requestUserOrders())

    const response = await api.userOrders(params)
    const result = response?.result

    if (result === 'success') {
      dispatch(successUserOrders({ ...response, mergeResult }))
    } else {
      dispatch(failureUserOrders(response))
    }

  } catch (err) {
    dispatch(failureUserOrders(err.toString()))
  }
}

export const fetchOrder = params => async (dispatch, _getState, { api }) => {
  try {
    dispatch(requestOrder())
    const response = await api.userOrder(params)
    const result = response?.result

    if (result === 'success') {
      const { data } = response
      dispatch(successOrder(data))
    } else {
      dispatch(failureOrder(response))
    }
  } catch (err) {
    dispatch(failureOrder(err.toString()))
  }
}

export const fetchDiscount = () => async (dispatch, _getState, { api }) => {
  try {
    dispatch(requestDiscount())
    const response = await api.userDiscount()
    const result = response?.result

    if (result === 'success') {

      const { discount } = response
      dispatch(successDiscount(discount))
    } else {
      dispatch(failureDiscount(response))
    }
  } catch (err) {
    dispatch(failureDiscount(err.toString()))
  }
}

export const fetchUserRefunds = ({ mergeResult, ...params } = {}) => async (dispatch, getState, { api }) => {
  try {
    dispatch(requestUserRefunds())

    const response = await api.userRefunds(params)
    const result = response?.result

    if (result === 'success') {
      dispatch(successUserRefunds({ ...response, mergeResult }))
    } else {
      dispatch(failureUserRefunds(response))
    }

  } catch (err) {
    dispatch(failureUserRefunds(err.toString()))
  }
}

export const fetchReturnableProducts = ({ mergeResult, ...params } = {}) => async (dispatch, getState, { api }) => {
  try {
    dispatch(requestReturnableProducts())

    const response = await api.returnableProducts(params)
    const result = response?.result

    if (result === 'success') {
      dispatch(successReturnableProducts({ ...response, mergeResult }))
    } else {
      dispatch(failureReturnableProducts(response))
    }

  } catch (err) {
    dispatch(failureReturnableProducts(err.toString()))
  }
}

export const fetchSendRefund = ({ params, onSuccess, onError }) => async (dispatch, getState, { api }) => {
  try {
    dispatch(requestSendRefund())

    const response = await api.sendRefund(params)
    const result = response?.result

    if (result === 'success') {
      dispatch(successSendRefund())
      if (onSuccess) onSuccess(response.data.hash)
    } else {
      if (onError) onError()
      dispatch(failureSendRefund(response))
    }

  } catch (err) {
    if (onError) onError()
    dispatch(failureSendRefund(err.toString()))
  }
}

export const fetchRefundInfo = ({ hash }) => async (dispatch, getState, { api }) => {
  try {
    dispatch(requestRefundInfo())

    const response = await api.refundInfo({ inlineSlug: hash })
    const result = response?.result

    if (result === 'success') {
      dispatch(successRefundInfo({ data: response.data, products: response.products }))
    } else {
      dispatch(failureRefundInfo(response))
    }

  } catch (err) {
    dispatch(failureRefundInfo(err.toString()))
  }
}

export const fetchCountriesOfDelivery = () => async (dispatch, getState, { api }) => {
  const state = getState().user.countriesOfDelivery

  try {
    if (!state.data.length) {
      dispatch(requestCountriesOfDelivery())
      const response = await api.countriesOfDelivery()
      const result = response?.result

      if (result === 'success') {

        const { data } = response
        dispatch(successCountriesOfDelivery(data))
      } else {
        dispatch(failureCountriesOfDelivery(response))
      }
    }
  } catch (err) {
    dispatch(failureCountriesOfDelivery(err.toString()))
  }
}

export const addedFavoriteBrand = ({ params, onSuccess, onError }) => async (dispatch, getState, { api }) => {
  try {
    dispatch(requestChangeFavoriteBrands())

    const response = await api.createFavoriteBrand(params)
    const result = response?.result

    if (result === 'success') {
      const prevData = getState().user.favoriteBrands.data;

      dispatch(successChangeFavoriteBrands([...prevData, response.data[0].brand_id]))
      if (onSuccess) onSuccess()
    } else {
      if (onError) onError()
      dispatch(failureChangeFavoriteBrands(response))
    }

  } catch (err) {
    if (onError) onError()
    dispatch(failureChangeFavoriteBrands(err.toString()))
  }
}

export const removeFavoriteBrand = ({ params, onSuccess, onError }) => async (dispatch, getState, { api }) => {
  try {
    dispatch(requestChangeFavoriteBrands())

    const response = await api.deleteFavoriteBrand(params)
    const result = response?.result

    if (result === 'success') {
      const prevData = getState().user.favoriteBrands.data;

      dispatch(successChangeFavoriteBrands(prevData.filter(item => item != params.brand_id)))
      if (onSuccess) onSuccess()
    } else {
      if (onError) onError()
      dispatch(failureChangeFavoriteBrands(response))
    }

  } catch (err) {
    if (onError) onError()
    dispatch(failureChangeFavoriteBrands(err.toString()))
  }
}

export const getAuthData = createSelector(
  state => state.user,
  user => {
    const {
      loggedIn,
      loggingIn,
      loggingOut,
      codeSending,
      codeSent,
      error,
      user: userData,
      redirectUrl,
      returnableItem,
      profile,
      hasNewProducts,
      isNewUser,
      subscribeError,
      emailValidateError,
      phoneValidateError,
      hasGotIP
    } = user
    return {
      loggedIn,
      loggingIn,
      loggingOut,
      codeSending,
      codeSent,
      error,
      user: userData,
      redirectUrl,
      returnableItem,
      profile,
      hasNewProducts,
      isNewUser,
      subscribeError,
      emailValidateError,
      phoneValidateError,
      hasGotIP,
    }
  },
)

export const getUserOrders = createSelector(
  state => state.user,
  user => {
    const { data, loading, error } = user.ordersHistory

    return {
      data,
      loading,
      error,
    }
  },
)

export const getOrder = createSelector(
  state => state.user,
  user => {
    const { data, loading, error } = user.order;

    return {
      data,
      loading,
      error,
    }
  },
)

export const getDiscount = createSelector(
  state => state.user,
  user => {
    const { data, loading, error } = user.discount;

    return {
      data,
      loading,
      error,
    }
  },
)

export const getUserRefunds = createSelector(
  state => state.user,
  user => {
    const { data, loading, error } = user.refundsHistory

    return {
      data,
      loading,
      error,
    }
  },
)

export const getSentRefundInfo = createSelector(
  state => state.user,
  user => {
    const { loading, error } = user.refund

    return {
      loading,
      error,
    }
  },
)

export const getRefundInfo = createSelector(
  state => state.user,
  user => {
    const { loading, error, data } = user.refundInfo

    return {
      data,
      loading,
      error,
    }
  },
)

export const getCountriesOfDelivery = createSelector(
  state => state.user,
  user => {
    const { data, loading, error } = user.countriesOfDelivery;

    return {
      data,
      loading,
      error,
    }
  },
)

export const getReturnableProducts = createSelector(
  state => state.user,
  user => {
    const { data, loading, error } = user.returnableProducts

    return {
      data,
      loading,
      error,
    }
  },
)

export const {
  requestChangeFavoriteBrands,
  successChangeFavoriteBrands,
  failureChangeFavoriteBrands,
  changeBanner,
  requestCode,
  successCode,
  failureCode,
  requestCountriesOfDelivery,
  successCountriesOfDelivery,
  failureCountriesOfDelivery,
  requestSendRefund,
  successSendRefund,
  failureSendRefund,
  requestRefundInfo,
  successRefundInfo,
  failureRefundInfo,
  requestReturnableProducts,
  successReturnableProducts,
  failureReturnableProducts,
  resetSendCodeState,
  requestLogin,
  successLogin,
  failureLogin,
  requestLogout,
  successLogout,
  requestRemoveProfile,
  successRemoveProfile,
  failureRemoveProfile,
  requestDiscount,
  successDiscount,
  failureDiscount,
  failureLogout,
  changeRedirectUrl,
  changeReturnableItem,
  requestProfileUpdate,
  successProfileUpdate,
  failureProfileUpdate,
  changeNewProducts,
  changeNewUserStatus,
  requestUserOrders,
  successUserOrders,
  failureUserOrders,
  requestUserRefunds,
  successUserRefunds,
  failureUserRefunds,
  requestOrder,
  successOrder,
  failureOrder,
  failureSubscribe,
  failureValidateEmail,
  failureValidatePhone,
  successGotIP,
  resetProfileError,
} = user.actions

export default user.reducer
